import { EditableComponent } from '@adobe/aem-react-editable-components';
import clsx from 'clsx';
import {
  ABody,
  ABodyColor,
  ABodySize,
  AHeaderSize,
  AHeaderTag,
  ASubheader,
  ASubheaderSize,
  MButton,
  MButtonSize,
  MMediaSelector,
  MTextBlockStandard,
  MTextBlockStandardAlignment,
} from '@marriott/mi-web-ui-kit';

import { StyledOBannerFeatureEditorial } from './OBannerFeatureEditorial.styles';
import { oBannerFeatureEditorialMockData } from './__mock__/OBannerFeatureEditorial.mock';
import { OBannerFeatureEditorialProps } from './OBannerFeatureEditorial.types';

const normalizeHeaderSize = (size?: string): AHeaderSize => {
  return Object.values(AHeaderSize).includes(size as AHeaderSize) ? (size as AHeaderSize) : AHeaderSize.TitleS;
};

const normalizeHeaderTag = (headerTag?: string): AHeaderTag => {
  const normalizedHeaderTag = headerTag?.toLowerCase();
  return Object.values(AHeaderTag).includes(normalizedHeaderTag as AHeaderTag)
    ? (normalizedHeaderTag as AHeaderTag)
    : AHeaderTag.h2;
};

const normalizeSubheaderSize = (size?: string): ASubheaderSize => {
  return Object.values(ASubheaderSize).includes(size as ASubheaderSize) ? (size as ASubheaderSize) : ASubheaderSize.L;
};

export const OBannerFeatureEditorial = ({
  styleclass,
  model: aemModel,
  trackingProperties,
  ...props
}: OBannerFeatureEditorialProps) => {
  const model = aemModel ?? oBannerFeatureEditorialMockData;
  const { mMediaSelector, mTextBlockStandard, aSubheader, aBody, mButton } = { ...props, ...model };

  const eyebrowText = mTextBlockStandard?.aEyebrow?.eyebrowText ?? mTextBlockStandard?.eyebrowText;
  const headerText = mTextBlockStandard?.aHeader?.text ?? mTextBlockStandard?.headerText;
  const headerSize = normalizeHeaderSize(mTextBlockStandard?.aHeader?.size ?? mTextBlockStandard?.headerSize);
  const headerTag = normalizeHeaderTag(mTextBlockStandard?.aHeader?.headerTag ?? mTextBlockStandard?.headerTag);
  const subheaderText = aSubheader?.text;
  const bodyText = aBody?.bodyText;
  const buttonText = mButton?.buttonText;
  const ctaLink = mButton?.ctaLink ?? mButton?.href;
  const openInANewTab = mButton?.openInANewTab;

  const hasContent = Boolean(mMediaSelector || eyebrowText || headerText || subheaderText || bodyText || buttonText);

  if (!hasContent) {
    return null;
  }

  return (
    <StyledOBannerFeatureEditorial
      data-testid="obanner-feature-editorial"
      data-component-name="o-awp-banner-feature-editorial"
      className={styleclass}
    >
      <div className="obanner-feature-editorial__inner">
        {mMediaSelector && <MMediaSelector {...mMediaSelector} className="obanner-feature-editorial__media" />}

        <div className="obanner-feature-editorial__content">
          <div className="obanner-feature-editorial__copy">
            {(eyebrowText || headerText) && (
              <MTextBlockStandard
                alignment={MTextBlockStandardAlignment.Center}
                eyebrowText={eyebrowText}
                headerText={headerText || ''}
                headerSize={headerSize}
                headerTag={headerTag}
                hasAccentLine={false}
              />
            )}

            {subheaderText && (
              <ASubheader
                text={subheaderText}
                size={normalizeSubheaderSize(aSubheader?.size)}
                className="obanner-feature-editorial__subheader"
              />
            )}

            {bodyText && (
              <ABody
                bodyText={bodyText}
                size={aBody?.size ?? ABodySize.M}
                color={aBody?.color ?? ABodyColor.Alternate}
                className="obanner-feature-editorial__body"
              />
            )}
          </div>

          {buttonText && (
            <MButton
              {...mButton}
              buttonText={buttonText}
              ctaLink={ctaLink}
              buttonType={mButton?.buttonType}
              size={mButton?.size ?? MButtonSize.M}
              openInANewTab={openInANewTab}
              trackingProperties={mButton?.trackingProperties ?? trackingProperties}
              className={clsx('obanner-feature-editorial__cta', mButton?.className)}
            />
          )}
        </div>
      </div>
    </StyledOBannerFeatureEditorial>
  );
};

export const OBannerFetaureEditorial = OBannerFeatureEditorial;

export const OBannerFeatureEditorialConfig = {
  emptyLabel: 'OBannerFeatureEditorial',
  isEmpty: () => false,
  resourceType: `mi-aem-utils/components/content/o-banner-feature-editorial`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const OBannerFeatureEditorialEditable = (props: any) => (
  <EditableComponent config={OBannerFeatureEditorialConfig} {...props}>
    <OBannerFeatureEditorial {...props} />
  </EditableComponent>
);

export const OBannerFetaureEditorialEditable = OBannerFeatureEditorialEditable;
